import React, { useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../../config/appBranches";
import { loadMonthlyAttendanceRows, formatPresentDayUnits } from "../../helpers/payrollAttendanceCore";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function aggregateYear(monthResults) {
  const map = new Map();
  for (const { monthIndex, rows } of monthResults) {
    if (!rows?.length) continue;
    for (const r of rows) {
      const id = r.employeeId;
      if (!map.has(id)) {
        map.set(id, {
          employeeId: id,
          employeeName: r.employeeName || id,
          department: String(r.department || ""),
          months: Array(12).fill(null),
          monthsAbsent: Array(12).fill(null),
          total: 0,
          absentTotal: 0,
        });
      }
      const agg = map.get(id);
      const pd = Number(r.presentDays) || 0;
      const ad = Array.isArray(r.absentDates) ? r.absentDates.length : 0;
      agg.months[monthIndex] = pd;
      agg.monthsAbsent[monthIndex] = ad;
      agg.employeeName = r.employeeName || agg.employeeName;
      if (r.department) agg.department = String(r.department);
    }
  }
  const list = Array.from(map.values()).map((row) => ({
    ...row,
    total: row.months.reduce((s, v) => s + (typeof v === "number" ? v : 0), 0),
    absentTotal: row.monthsAbsent.reduce((s, v) => s + (typeof v === "number" ? v : 0), 0),
  }));
  list.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  return list;
}

export default function PayrollYearlyAttendancePage() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);
  const branchPool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;

  const [year, setYear] = useState(() => new Date().getFullYear());
  const [shopLabel, setShopLabel] = useState(branchPool[0]?.label || ATTENDANCE_BRANCHES[0].label);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [showAbsentList, setShowAbsentList] = useState(true);
  const [showPresentList, setShowPresentList] = useState(true);

  const currentShop = useMemo(
    () => branchPool.find((b) => b.label === shopLabel) || branchPool[0],
    [branchPool, shopLabel]
  );

  const shiftYear = (delta) => setYear((y) => y + delta);

  const load = useCallback(async () => {
    if (!currentShop) return;
    setLoading(true);
    setRows([]);
    setMeta(null);
    const monthResults = [];
    let lastDiag = null;
    try {
      for (let m = 0; m < 12; m++) {
        try {
          const pack = await loadMonthlyAttendanceRows(currentShop, new Date(year, m, 1));
          monthResults.push({ monthIndex: m, rows: pack.rows });
          lastDiag = {
            punchCollection: pack.punchCollection,
            employeeCollection: pack.employeeCollection,
            attendanceLoadMode: pack.attendanceLoadMode,
          };
        } catch (e) {
          console.error(e);
          toast.error(`${MONTH_LABELS[m]} ${year}: ${e?.message || "Failed"}`);
          monthResults.push({ monthIndex: m, rows: [] });
        }
      }
      const agg = aggregateYear(monthResults);
      setRows(agg);
      setMeta(lastDiag);
      toast.success(`Loaded ${year} (${agg.length} employees)`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to load year");
    } finally {
      setLoading(false);
    }
  }, [currentShop, year]);

  const exportXlsx = () => {
    if (!rows.length) {
      toast.error("Nothing to export");
      return;
    }
    if (!showPresentList && !showAbsentList) {
      toast.error("Select Present list or Absent list");
      return;
    }
    const data = rows.map((r) => {
      const o = {
        Employee: r.employeeName,
        Department: r.department || "",
      };
      if (showPresentList) {
        MONTH_LABELS.forEach((label, i) => {
          const v = r.months[i];
          o[`Present_${label}`] = typeof v === "number" ? v : "";
        });
        o.Present_year_total = r.total;
      }
      if (showAbsentList) {
        MONTH_LABELS.forEach((label, i) => {
          const v = r.monthsAbsent[i];
          o[`Absent_${label}`] = typeof v === "number" ? v : "";
        });
        o.Absent_year_total = r.absentTotal;
      }
      return o;
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "YearlyAttendance");
    const br = String(currentShop?.label || "branch").replace(/\s+/g, "-");
    XLSX.writeFile(wb, `payroll-yearly-attendance-${year}-${br}.xlsx`);
    toast.success("Download started");
  };

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-[100rem] px-4 py-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll</p>
          <h1 className="text-2xl font-bold text-slate-900">Yearly attendance</h1>
          <p className="mt-1 text-sm text-slate-600">
            Present-day units per month (same rules as the monthly payroll screen). Absent list counts monthly absent
            days from attendance calculation.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button type="button" onClick={() => shiftYear(-1)} className="rounded-lg p-1 hover:bg-slate-100" aria-label="Previous year">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="min-w-[5rem] text-center text-sm font-semibold tabular-nums">{year}</span>
            <button type="button" onClick={() => shiftYear(1)} className="rounded-lg p-1 hover:bg-slate-100" aria-label="Next year">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <label className="text-xs font-semibold text-slate-700">
            Branch
            <select
              value={shopLabel}
              onChange={(e) => setShopLabel(e.target.value)}
              className="mt-1 block min-w-[10rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            >
              {branchPool.map((b) => (
                <option key={b.label} value={b.label}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Loading year…" : "Load year"}
          </button>
          <button
            type="button"
            onClick={exportXlsx}
            disabled={!rows.length}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden />
            Export XLSX
          </button>
          <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={showAbsentList}
              onChange={(e) => setShowAbsentList(e.target.checked)}
              className="rounded border-slate-400 text-indigo-600 focus:ring-indigo-500/30"
            />
            Absent list
          </label>
          <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={showPresentList}
              onChange={(e) => setShowPresentList(e.target.checked)}
              className="rounded border-slate-400 text-indigo-600 focus:ring-indigo-500/30"
            />
            Present list
          </label>
        </div>

        {meta && (
          <p className="mt-3 text-[11px] text-slate-500">
            Punch data: <span className="font-mono">{meta.punchCollection}</span> · Employees:{" "}
            <span className="font-mono">{meta.employeeCollection}</span>
            {meta.attendanceLoadMode ? (
              <>
                {" "}
                · Mode: <span className="font-mono">{meta.attendanceLoadMode}</span>
              </>
            ) : null}
          </p>
        )}

        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-[11px]">
            <thead className="bg-slate-800 text-[9px] font-bold uppercase tracking-wide text-slate-100">
              <tr>
                <th rowSpan={2} className="sticky left-0 z-10 bg-slate-800 px-2 py-2 align-bottom">
                  Employee
                </th>
                <th rowSpan={2} className="px-2 py-2 align-bottom">
                  Dept
                </th>
                {showPresentList ? (
                  <>
                    <th colSpan={12} className="border-b border-slate-600 px-1 py-1 text-center font-bold">
                      Present (days)
                    </th>
                    <th rowSpan={2} className="px-1 py-2 text-right align-bottom whitespace-nowrap">
                      Σ Pr.
                    </th>
                  </>
                ) : null}
                {showAbsentList ? (
                  <>
                    <th colSpan={12} className="border-b border-slate-600 px-1 py-1 text-center font-bold">
                      Absent (days)
                    </th>
                    <th rowSpan={2} className="px-1 py-2 text-right align-bottom whitespace-nowrap">
                      Σ Ab.
                    </th>
                  </>
                ) : null}
              </tr>
              <tr>
                {showPresentList
                  ? MONTH_LABELS.map((lab) => (
                      <th key={`p-${lab}`} className="px-1 py-1 text-right">
                        {lab}
                      </th>
                    ))
                  : null}
                {showAbsentList
                  ? MONTH_LABELS.map((lab) => (
                      <th key={`l-${lab}`} className="px-1 py-1 text-right">
                        {lab}
                      </th>
                    ))
                  : null}
              </tr>
            </thead>
            <tbody>
              {!showPresentList && !showAbsentList ? (
                <tr>
                  <td colSpan={28} className="px-4 py-8 text-center text-slate-500">
                    Tick Absent list or Present list to view data.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={28} className="px-4 py-8 text-center text-slate-500">
                    Choose branch and year, then Load year.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.employeeId} className="border-t border-slate-100">
                    <td className="sticky left-0 z-[1] bg-white px-2 py-1">
                      <div className="max-w-[14rem] truncate font-medium whitespace-nowrap" title={r.employeeName}>
                        {r.employeeName}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-slate-600">
                      <div className="max-w-[8rem] truncate whitespace-nowrap" title={r.department}>
                        {r.department || "—"}
                      </div>
                    </td>
                    {showPresentList
                      ? MONTH_LABELS.map((_, mi) => {
                          const v = r.months[mi];
                          return (
                            <td key={`p-${mi}`} className="px-1 py-1 text-right tabular-nums text-slate-800">
                              {typeof v === "number" ? formatPresentDayUnits(v) : "—"}
                            </td>
                          );
                        })
                      : null}
                    {showPresentList ? (
                      <td className="px-1 py-1 text-right text-sm font-semibold tabular-nums text-slate-900">
                        {formatPresentDayUnits(r.total)}
                      </td>
                    ) : null}
                    {showAbsentList
                      ? MONTH_LABELS.map((_, mi) => {
                          const v = r.monthsAbsent[mi];
                          return (
                            <td key={`l-${mi}`} className="px-1 py-1 text-right tabular-nums text-teal-900">
                              {typeof v === "number" ? String(v) : "—"}
                            </td>
                          );
                        })
                      : null}
                    {showAbsentList ? (
                      <td className="px-1 py-1 text-right text-sm font-semibold tabular-nums text-teal-900">
                        {r.absentTotal}
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
